import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { alpha } from '@mui/material/styles'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import PropTypes from 'prop-types'
import * as React from 'react'

import TableSortLabel from '@mui/material/TableSortLabel'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { visuallyHidden } from '@mui/utils'
import SelectCategory from './SelectCategory'
import { DataGrid } from '@mui/x-data-grid'
import Stack from '@mui/material/Stack'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { convertToUserDateFormate } from 'utils/helpers'
import { useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

export default function BasicTable({
  loading,
  operations,
  updateLoading,
  categories,
  fetchOperations,
  emptyLoad,
  fetchEmptyOperations,
}) {
  return (
    <EnhancedTable
      operations={operations}
      loading={loading}
      emptyLoad={emptyLoad}
      updateLoading={updateLoading}
      categories={categories}
      fetchOperations={fetchOperations}
      fetchEmptyOperations={fetchEmptyOperations}
    />
  )
}

function createData(name, calories, fat, carbs, protein) {
  return {
    name,
    calories,
    fat,
    carbs,
    protein,
  }
}

const rows = [
  createData('Cupcake', 305, 3.7, 67, 4.3),
  createData('Donut', 452, 25.0, 51, 4.9),
  createData('Eclair', 262, 16.0, 24, 6.0),
  createData('Frozen yoghurt', 159, 6.0, 24, 4.0),
  createData('Gingerbread', 356, 16.0, 49, 3.9),
  createData('Honeycomb', 408, 3.2, 87, 6.5),
  createData('Ice cream sandwich', 237, 9.0, 37, 4.3),
  createData('Jelly Bean', 375, 0.0, 94, 0.0),
  createData('KitKat', 518, 26.0, 65, 7.0),
  createData('Lollipop', 392, 0.2, 98, 0.0),
  createData('Marshmallow', 318, 0, 81, 2.0),
  createData('Nougat', 360, 19.0, 9, 37.0),
  createData('Oreo', 437, 18.0, 63, 4.0),
]

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

const headCells = [
  {
    id: 'Date',
    numeric: false,
    disablePadding: true,
    label: 'Date',
  },
  {
    id: 'NumeroCompte',
    numeric: false,
    disablePadding: false,
    label: 'NumeroCompte',
  },
  {
    id: 'Libelle',
    numeric: true,
    disablePadding: false,
    label: 'Libelle',
  },
  {
    id: 'Montant',
    numeric: true,
    disablePadding: false,
    label: 'Montant',
    type: 'number',
  },
  {
    id: 'Categories',
    numeric: true,
    disablePadding: false,
    label: 'Categories',
  },
]

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'left' : 'center'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
}

const EnhancedTableToolbar = (props) => {
  const { numSelected } = props
  const { t } = useTranslation('layout') // LAP-733 (GE022)

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
          {t('finance.categorisation.countSelected', { count: numSelected })}
        </Typography>
      ) : (
        <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
          {t('finance.categorisation.summaryTitle')}
        </Typography>
      )}
    </Toolbar>
  )
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
}

function CustomNoRowsOverlay() {
  const { t } = useTranslation('layout')
  return (
    <Stack
      height="100%"
      alignItems="center"
      justifyContent="center"
      spacing={1}
      sx={{ p: 3, textAlign: 'center' }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t('finance.categorisation.noOperationsTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('finance.categorisation.noOperationsHelper')}
      </Typography>
    </Stack>
  )
}

function ListOperationsOfTable({
  fetchOperations,
  operations,
  setSelectedRows,
  setOperations,
  updateLoading,
  categories,
}) {
  const theme = useTheme()
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [modifiedColumns, setModifiedColumns] = React.useState([])

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const siteUser = useSelector((store) => store.user)

  const [rows, setRows] = React.useState(20)
  const [page, setPage] = React.useState(0)
  let navigate = useNavigate()
  const dayInMonthComparator = (v1, v2) => v1.getDate() - v2.getDate()
  const columns = [
    {
      field: 'Date',
      headerName: t('finance.categorisation.columnDate'),
      width: 130,
      type: 'date',
      valueGetter: (params) => convertToUserDateFormate(siteUser.user, params.row.Date),
      // sortComparator: dayInMonthComparator,
    },
    {
      field: 'NumeroCompte',
      headerName: t('finance.categorisation.columnAccount'),
      width: 160,
    },
    {
      field: 'Libelle',
      headerName: t('finance.categorisation.columnLabel'),
      width: 730,
      renderCell: (params) => {
        return <Box>{params.row.Libelle}</Box>
      },
    },
    {
      field: 'Montant',
      headerName: t('finance.categorisation.columnAmount'),
      width: 100,
      type: 'number',
      sortComparator: (v1, v2) => parseInt(v1) - parseInt(v2),
    },

    {
      field: 'Categorie',
      headerName: t('finance.categorisation.columnCategory'),
      width: 300,
      renderCell: (params) => {
        return (
          <SelectCategory updateLoading={updateLoading} categories={categories} row={params.row} />
        )
      },
    },
  ]

  React.useEffect(() => {
    if (isMobile) {
      setModifiedColumns([
        {
          field: 'Libelle',
          headerName: t('finance.categorisation.columnLabel'),
          width: 210,

          renderCell: (params) => (
            <Box py={2} mt={1}>
              <Box fontWeight={600}>{params.row.Libelle}</Box>
              <Box>
                <Box component={'span'}>{t('finance.categorisation.columnDate')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {convertToUserDateFormate(siteUser.user, params.row.Date)}
                </Box>
              </Box>
              <Box>
                <Box component={'span'}>{t('finance.categorisation.columnAccount')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.NumeroCompte}
                </Box>
              </Box>
              <Box>
                <Box component={'span'}>{t('finance.categorisation.columnAmount')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.Montant} {params.row?.NCompte?.Devise}
                </Box>
              </Box>
            </Box>
          ),
        },
        {
          field: 'Categorie',
          headerName: t('finance.categorisation.columnCategory'),
          width: 160,

          renderCell: (params) => (
            <Box py={2}>
              <SelectCategory
                updateLoading={updateLoading}
                categories={categories}
                row={params.row}
              />
            </Box>
          ),
        },
      ])
    } else {
      setModifiedColumns(columns)
    }
  }, [isMobile, categories])
  const handleRowsChange = (newPageSize) => {
    setRows(newPageSize)
    let url = `?limit=${newPageSize}&offset=${page}`
    fetchOperations(url)
  }
  const handlePageChange = (newPage) => {
    setPage(newPage * rows)
    let url = `?limit=${rows}&offset=${newPage * rows}`
    fetchOperations(url)
  }

  return (
    <div
      style={{
        height: 700,
        width: '100%',
        background: 'white',
        borderRadius: '5px',
      }}
    >
      <DataGrid
        sx={{
          '.MuiTablePagination-toolbar': {
            alignItems: 'baseline',
          },
          '--DataGrid-overlayHeight': '300px',
        }}
        rows={operations.results ? operations.results : []}
        columns={modifiedColumns}
        pageSize={rows}
        // rowHeight={isMobile ? 120 : 50}
        getRowHeight={() => (isMobile ? 'auto' : 50)}
        rowCount={operations.count ? operations.count : 0}
        onPageSizeChange={handleRowsChange}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[10, 20, 40]}
        pagination
        onSelectionModelChange={(itm) => setSelectedRows([...itm])}
        paginationMode="server"
        components={{
          NoRowsOverlay: CustomNoRowsOverlay,
          NoResultsOverlay: CustomNoRowsOverlay,
        }}
        // checkboxSelection
        disableSelectionOnClick
      />
    </div>
  )
}
function EnhancedTable({
  loading,
  operations,
  updateLoading,
  categories,

  fetchOperations,
  emptyLoad,
  fetchEmptyOperations,
}) {
  const [order, setOrder] = React.useState('asc')
  const [orderBy, setOrderBy] = React.useState('calories')
  const [selected, setSelected] = React.useState([])
  const [page, setPage] = React.useState(0)
  const [dense, setDense] = React.useState(false)
  const [rowsPerPage, setRowsPerPage] = React.useState(20)

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  // const updateLoading = () => {}
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = operations.results.map((n) => n.name)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    fetchOperations(rowsPerPage, rowsPerPage * newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    // setRowsPerPage(parseInt(event.target.value));
    // setPage(0);
    setRowsPerPage(rowsPerPage + 20)

    if (emptyLoad) {
      fetchEmptyOperations(rowsPerPage + 20)
    } else {
      fetchOperations(rowsPerPage + 20)
    }
  }

  const handleChangeDense = (event) => {
    setDense(event.target.checked)
  }

  const isSelected = (name) => selected.indexOf(name) !== -1

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <ListOperationsOfTable
          fetchOperations={fetchOperations}
          operations={operations}
          categories={categories}
          updateLoading={updateLoading}
        />
      </Paper>
    </Box>
  )
}
